import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
  MenuItem,
  Select,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from '@mui/material';
import { CatalogPagination } from '../components/CatalogPagination';
import {
  Close as CloseIcon,
  BookmarkBorder,
  ViewModule,
  ViewList,
  Image as ImageIcon,
  History as HistoryIcon,
  Palette,
  Person,
} from '@mui/icons-material';
import { useSearch } from '../hooks/useSearch';
import { useBookmarks } from '../hooks/useBookmarks';
import { usePermissions } from '../hooks/usePermissions';
import { useSavedSearches } from '../hooks/useSavedSearches';
import {
  SearchFilterRail,
  type FacetKey,
} from '../components/search/SearchFilterRail';
import { ScopeSelector } from '../components/search/ScopeSelector';
import { SearchResultsGrid } from '../components/SearchResultsGrid';
import { SearchResultsList } from '../components/SearchResultsList';
import { SaveSearchDialog } from '../components/SaveSearchDialog';
import { UsersTabPanel } from '../components/search/UsersTabPanel';
import type { UserResult } from '../components/search/UserResultCard';
// Single source of truth for the search sort options (GH#1193) — shared with the
// Home results toolbar so the two search surfaces never drift out of parity with
// each other or the Catalog (My Objects) sort.
import { SEARCH_SORT_OPTIONS } from '../components/SearchResultsToolbar';
import type { SearchFilters, SortBy, SearchScope } from '../contexts/SearchContext';
import { parseScopes, serializeScopes } from '../utils/searchScopes';
import api from '../utils/api';

const FACET_KEYS: FacetKey[] = ['workType', 'techniques', 'materials', 'creators', 'decade'];
// `catalog` is a non-FacetKey facet — its buckets feed the ScopeSelector
// counts rather than a facet-rail section, so it lives in the request set
// but not in FACET_KEYS.
const REQUESTED_FACETS = [...FACET_KEYS, 'catalog'];

type ViewMode = 'grid' | 'list' | 'image';

const PAGE_SIZE_OPTIONS = [12, 24, 48] as const;

// Global search default: when the user hasn't explicitly narrowed via the
// ScopeSelector, search EVERYTHING they can access — own items across all
// owned catalogs, all public items (any owner), and every delegated catalog —
// independent of the active-catalog selection. Kept out of `selectedScopes`
// state so `hasIntent` (and the empty-state guard) stay driven by real user
// intent; the union is applied only when a search actually runs.
const GLOBAL_SEARCH_SCOPES: SearchScope[] = ['own', 'public', 'delegate'];

const VIEW_MODE_STORAGE_KEY = 'search:viewMode';

const readStoredViewMode = (): ViewMode => {
  try {
    const v = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (v === 'grid' || v === 'list' || v === 'image') return v;
  } catch {
    /* SSR or privacy-mode — fall back to default */
  }
  return 'grid';
};

/**
 * Dedicated Search page (flag-gated via enableSearchPageRedesign).
 *
 * Phase 4: adds the toolbar (sort/per-page/view toggle), Save-search button,
 * ⌘K/Ctrl+K keyboard shortcut, and a populated empty state showing recent
 * and saved searches.
 */
const SearchPage: React.FC = () => {
  const {
    searchTerm,
    setSearchTerm,
    search,
    searchResults,
    isSearching,
    error,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    facetBuckets,
    sortBy,
    setSortBy,
  } = useSearch();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const { permissions } = usePermissions();
  const { isBookmarked, toggleBookmark } = useBookmarks();
  const { savedSearches, saveSearch } = useSavedSearches();

  const [activeTab, setActiveTab] = useState<'objects' | 'users'>(
    searchParams.get('tab') === 'users' ? 'users' : 'objects',
  );

  const [userResults, setUserResults] = useState<UserResult[]>([]);
  const [userTotal, setUserTotal] = useState(0);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [userSearchError, setUserSearchError] = useState<string | null>(null);

  // Stable identity (deps `[]`) — query is passed as an argument, not closed
  // over. Don't add `searchTerm` to deps; doing so would re-create the
  // callback every keystroke and re-fire the user-search effect endlessly.
  const searchUsers = useCallback(async (query: string): Promise<void> => {
    setIsSearchingUsers(true);
    setUserSearchError(null);
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      params.append('size', '50');
      const response = await api.get(`/api/search/users?${params.toString()}`);
      const items: UserResult[] = (response.data?.items ?? []).map((item: Record<string, unknown>) => ({
        userId: item.userId as string,
        name: (item.name as string) || 'Unknown User',
        email: item.email as string | undefined,
        userType: item.userType as string | undefined,
        aliId: item.aliId as string | undefined,
        artworkCount: (item.artworkCount as number) ?? 0,
        profileImageUrl: item.profileImageUrl as string | undefined,
        createdAt: item.createdAt as string | undefined,
      }));
      setUserResults(items);
      setUserTotal(typeof response.data?.total === 'number' ? response.data.total : items.length);
    } catch (err: unknown) {
      setUserSearchError(err instanceof Error ? err.message : 'Failed to search users');
      setUserResults([]);
      setUserTotal(0);
    } finally {
      setIsSearchingUsers(false);
    }
  }, []);

  const [selectedFacets, setSelectedFacets] = useState<Record<FacetKey, Set<string>>>({
    workType: new Set(),
    materials: new Set(),
    techniques: new Set(),
    creators: new Set(),
    decade: new Set(),
  });
  // Phase 2 follow-up B: scope selector. Default empty array = no `?scopes=`
  // param sent → backend preserves legacy default behavior. The SearchContext
  // contract (line ~204) only appends the param when the array is non-empty.
  const [selectedScopes, setSelectedScopes] = useState<SearchScope[]>([]);
  const [viewMode, setViewModeState] = useState<ViewMode>(readStoredViewMode);
  const setViewMode = useCallback((next: ViewMode) => {
    setViewModeState(next);
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, next);
    } catch {
      /* storage unavailable — non-fatal */
    }
  }, []);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  // Baseline = total for the current query with NO facets applied. We capture
  // it whenever a search runs with zero active filters, then reuse it once
  // the user starts narrowing so we can show "— narrowed from N". A null
  // baseline (e.g. user landed straight into a filtered URL) hides the hint
  // rather than guessing.
  const [baselineTotal, setBaselineTotal] = useState<number | null>(null);
  const [baselineKey, setBaselineKey] = useState<string>('');

  const handleApplySavedSearch = useCallback(
    (s: { searchParams: { q?: string; scopes?: string } }) => {
      setSearchTerm(s.searchParams.q ?? '');
      setSelectedScopes(s.searchParams.scopes ? parseScopes(s.searchParams.scopes) : []);
      setPage(0);
    },
    [setSearchTerm, setSelectedScopes, setPage],
  );

  const activeFilterCount = useMemo(
    () => FACET_KEYS.reduce((sum, k) => sum + selectedFacets[k].size, 0),
    [selectedFacets],
  );

  // The hierarchical `catalog` facet is consumed verbatim by ScopeSelector,
  // which renders one row per bucket (own → each delegated catalog by name →
  // public) plus expandable Public/Private children. Buckets are already
  // sorted canonically by the backend, so no client-side reshaping is needed.
  const catalogBuckets = facetBuckets?.catalog ?? null;
  // Scopes count toward "intent" so a user who lands on /search and clicks a
  // single scope toggle (without typing or facets) still triggers a fetch.
  const hasIntent = searchTerm !== '' || activeFilterCount > 0 || selectedScopes.length > 0;

  // Sync `?q=` from the URL into searchTerm. Runs on every searchParams
  // change so a navigation from SearchBar (`navigate('/search?q=NEW')`)
  // updates the term + triggers the auto-fetch below — without this the
  // user submits a new query while staying on /search, the URL flips, but
  // searchTerm stays at the previous value and stale results linger.
  useEffect(() => {
    const q = searchParams.get('q') ?? '';
    if (q !== searchTerm) {
      setSearchTerm(q);
    }
    // searchTerm is intentionally omitted: the inverse sync (searchTerm →
    // URL) lives in the effect below, and adding it here would create a
    // loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // One-time scope seeding from the URL. Scope tokens don't change
  // mid-session via SearchBar (only via the sidebar tree), so a single seed
  // on mount is enough — re-running on every searchParams change would
  // fight the user's mid-session scope toggles.
  const didSeedScopesFromUrl = useRef(false);
  useEffect(() => {
    if (didSeedScopesFromUrl.current) return;
    didSeedScopesFromUrl.current = true;
    const scopesRaw = searchParams.get('scopes');
    if (scopesRaw) {
      const parsed = parseScopes(scopesRaw);
      if (parsed.length > 0) {
        setSelectedScopes(parsed);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSearch = useCallback(
    async (overrides?: Partial<SearchFilters> & { queryOverride?: string }) => {
      // Each facet sends every selected value (multi-select OR-within-group).
      // The backend re-applies all OTHER facet selections per facet agg, so
      // unselected options stay visible with cross-facet AND counts.
      const setToArray = <T,>(set: Set<T>): T[] | undefined =>
        set.size > 0 ? [...set] : undefined;

      const { queryOverride, ...filterOverrides } = overrides ?? {};
      const filters: SearchFilters = {
        requestedFacets: REQUESTED_FACETS,
        workType: setToArray(selectedFacets.workType),
        materials: setToArray(selectedFacets.materials),
        techniques: setToArray(selectedFacets.techniques),
        creators: setToArray(selectedFacets.creators),
        decade: setToArray(selectedFacets.decade),
        // Thread the scope selection. An explicit ScopeSelector choice wins;
        // otherwise default to the global union so search spans everything the
        // user can access (and SearchContext suppresses active-catalog
        // narrowing because scopes are present). Never send `undefined` here —
        // that would fall back to legacy active-catalog scoping.
        scopes: selectedScopes.length > 0 ? selectedScopes : GLOBAL_SEARCH_SCOPES,
        ...filterOverrides,
      };
      // queryOverride lets callers (notably the URL-driven auto-fetch effect)
      // bypass the closure's possibly-stale `searchTerm` and search with a
      // fresh value directly from the source of truth (the URL).
      const q = queryOverride !== undefined ? queryOverride : searchTerm;
      await search(q, filters);
    },
    [search, searchTerm, selectedFacets, selectedScopes],
  );

  // The URL's `?q=` param is the canonical "submitted query" — SearchBar
  // navigates here on Enter, so a change to this string is a strong signal
  // the user wants a fresh fetch. We use it directly (instead of `searchTerm`)
  // because the navbar input mutates context.searchTerm on every keystroke;
  // depending on it would trigger a fetch per keypress.
  const submittedQ = searchParams.get('q') ?? '';

  // Re-search when the submitted query, facet selection, page, sort, page
  // size, or scopes change. The `queryOverride` passes the URL value
  // directly to bypass any closure staleness around `searchTerm`. Guard:
  // don't auto-fetch on a bare /search with no query and no filters — that
  // used to surface "every item" and defeat the empty state.
  useEffect(() => {
    if (!hasIntent) return;
    runSearch({ queryOverride: submittedQ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedQ, selectedFacets, selectedScopes, page, sortBy, pageSize, hasIntent]);

  // Capture the unfiltered total per query as the "narrowed from" baseline.
  // We tie it to a key (query text) so a new query resets the baseline; once
  // the user adds a filter we keep the captured value to render the hint.
  useEffect(() => {
    if (isSearching) return;
    if (activeFilterCount === 0 && baselineKey !== searchTerm) {
      setBaselineTotal(total);
      setBaselineKey(searchTerm);
    }
  }, [isSearching, activeFilterCount, total, searchTerm, baselineKey]);

  // Reflect the active query in `?q=` so refresh + back/forward stay coherent
  // and the URL is shareable. Skip when nothing's typed to avoid an empty
  // `?q=` cluttering history.
  //
  // The skip-first-render ref is load-bearing: on browser refresh of
  // /search?q=foo the inverse URL→state sync (above) and this effect both
  // fire in the same commit phase using the same render's closures. This
  // effect's closure sees the still-empty initial `searchTerm` and would
  // delete `?q=foo` from the URL while URL→state is queueing
  // setSearchTerm('foo'). The two never converge — they alternate every
  // render — firing competing search requests until the page crashes. By
  // skipping the first run we let URL→state settle once before this writer
  // becomes active. After that, every render's closure has the latest
  // `searchTerm` and the inequality guard is enough.
  const isFirstQWriteRef = useRef(true);
  useEffect(() => {
    if (isFirstQWriteRef.current) {
      isFirstQWriteRef.current = false;
      return;
    }
    const current = searchParams.get('q') ?? '';
    if (current === searchTerm) return;
    const next = new URLSearchParams(searchParams);
    if (searchTerm) {
      next.set('q', searchTerm);
    } else {
      next.delete('q');
    }
    setSearchParams(next, { replace: true });
  }, [searchTerm, searchParams, setSearchParams]);

  // Mirror selectedScopes → `?scopes=` for refresh + share survival.
  // Empty selection deletes the param (matches the SearchContext contract:
  // empty array = omit, since backend rejects present-but-empty with 400).
  // Same skip-first-render guard as the q-writer above — the seeding effect
  // (above) updates `selectedScopes` from the URL on first render, but its
  // setState is queued; this writer's closure would otherwise see the empty
  // initial array and delete the URL param it just read.
  const isFirstScopesWriteRef = useRef(true);
  useEffect(() => {
    if (isFirstScopesWriteRef.current) {
      isFirstScopesWriteRef.current = false;
      return;
    }
    const current = searchParams.get('scopes') ?? '';
    const desired = selectedScopes.length > 0 ? serializeScopes(selectedScopes) : '';
    if (current === desired) return;
    const next = new URLSearchParams(searchParams);
    if (desired) {
      next.set('scopes', desired);
    } else {
      next.delete('scopes');
    }
    setSearchParams(next, { replace: true });
  }, [selectedScopes, searchParams, setSearchParams]);

  // User search runs independently of facets/scopes/page/sort/pageSize since
  // those filters don't apply to the user index. Keying on `searchTerm` alone
  // avoids a wasteful /api/search/users hit on every facet toggle and means a
  // 5xx on one endpoint can't tank the other tab.
  useEffect(() => {
    const q = searchTerm.trim();
    if (!q) {
      setUserResults([]);
      setUserTotal(0);
      setUserSearchError(null);
      return;
    }
    searchUsers(q);
  }, [searchTerm, searchUsers]);

  // Mirror activeTab → `?tab=users` (omit when on Objects so the URL stays
  // clean by default).
  useEffect(() => {
    const current = searchParams.get('tab') ?? '';
    const desired = activeTab === 'users' ? 'users' : '';
    if (current === desired) return;
    const next = new URLSearchParams(searchParams);
    if (desired) {
      next.set('tab', desired);
    } else {
      next.delete('tab');
    }
    setSearchParams(next, { replace: true });
  }, [activeTab, searchParams, setSearchParams]);

  const handleToggleFacet = useCallback(
    (facet: FacetKey, value: string) => {
      setSelectedFacets((prev) => {
        const next = new Set(prev[facet]);
        if (next.has(value)) {
          next.delete(value);
        } else {
          next.add(value);
        }
        return { ...prev, [facet]: next };
      });
      setPage(0);
    },
    [setPage],
  );

  const handleClearAll = useCallback(() => {
    setSelectedFacets({
      workType: new Set(),
      materials: new Set(),
      techniques: new Set(),
      creators: new Set(),
      decade: new Set(),
    });
    setPage(0);
  }, [setPage]);

  const handleClearQuery = () => {
    setSearchTerm('');
    setPage(0);
    runSearch({ requestedFacets: REQUESTED_FACETS });
  };

  const handleSaveSearch = async (name: string) => {
    // Project the current filter state into the SearchParams shape
    // accepted by the saved-searches API. Multi-value filters are sent
    // as comma-separated strings (matching the existing contract).
    await saveSearch(name, {
      q: searchTerm || undefined,
      workType:
        selectedFacets.workType.size === 1 ? [...selectedFacets.workType][0] : undefined,
      materials:
        selectedFacets.materials.size > 0
          ? [...selectedFacets.materials].join(',')
          : undefined,
      techniques:
        selectedFacets.techniques.size > 0
          ? [...selectedFacets.techniques].join(',')
          : undefined,
      creators:
        selectedFacets.creators.size > 0 ? [...selectedFacets.creators].join(',') : undefined,
      sortBy: sortBy !== 'relevance' ? sortBy : undefined,
      // Phase 2 follow-up B polish: persist scope selection in the same
      // wire form the URL uses, so a future "apply saved search" path is
      // a single parseScopes() call away from restoring the toggle state.
      scopes: selectedScopes.length > 0 ? serializeScopes(selectedScopes) : undefined,
    });
  };

  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const showEmptyState = !hasIntent;
  const showNoResults = !isSearching && hasIntent && searchResults.length === 0;
  const canSave = hasIntent;

  return (
    <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, sm: 3.5 } }}>
      <Box sx={{ mb: 1 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'serif',
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '2.25rem' },
            color: 'primary.main',
            m: 0,
          }}
        >
          Search
        </Typography>
        {(searchTerm !== '' || activeFilterCount > 0) && (
          <Typography
            sx={{
              fontSize: '1rem',
              color: 'text.secondary',
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {searchTerm !== '' ? (
              <>
                Results for{' '}
                <Box
                  component="em"
                  sx={{
                    fontFamily: 'serif',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    color: 'primary.main',
                  }}
                >
                  &ldquo;{searchTerm}&rdquo;
                </Box>
              </>
            ) : (
              'Showing filtered results'
            )}
            <Button
              size="small"
              onClick={handleClearQuery}
              sx={{
                ml: 1,
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: 'text.secondary',
                textTransform: 'none',
                minWidth: 0,
                p: 0.25,
              }}
              startIcon={<CloseIcon fontSize="small" />}
            >
              Clear
            </Button>
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '288px 1fr' },
          gap: 3.5,
          alignItems: 'start',
        }}
      >
        <SearchFilterRail
          buckets={facetBuckets}
          selected={selectedFacets}
          onToggle={handleToggleFacet}
          onClearAll={handleClearAll}
          topSlot={
            <ScopeSelector
              value={selectedScopes}
              onChange={setSelectedScopes}
              catalogBuckets={catalogBuckets}
            />
          }
        />

        <Box sx={{ minWidth: 0 }}>
          {/* Combined row: Tabs (Objects | Users) on the left; toolbar
              (sort, per-page, view, Save search) on the right. The row's
              bottom border doubles as the tab "track" so the active-tab
              underline stays flush. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              mb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_e, v) => setActiveTab(v as 'objects' | 'users')}
              data-testid="search-tabs"
              sx={{
                minHeight: 0,
                '& .MuiTabs-indicator': { bottom: -1 },
                '& .MuiTab-root': { textTransform: 'none' },
              }}
            >
              <Tab
                value="objects"
                icon={<Palette fontSize="small" />}
                iconPosition="start"
                label={hasIntent ? `Objects (${total})` : 'Objects'}
              />
              <Tab
                value="users"
                icon={<Person fontSize="small" />}
                iconPosition="start"
                label={hasIntent ? `Users (${userTotal})` : 'Users'}
              />
            </Tabs>

            <Box sx={{ flex: 1 }} />

            {activeTab === 'objects' && (
              <>
                <Select
                  size="small"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortBy)}
                  data-testid="sort-select"
                  sx={{ minWidth: 160 }}
                >
                  {SEARCH_SORT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  size="small"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  data-testid="per-page-select"
                  sx={{ minWidth: 128 }}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n} per page
                    </MenuItem>
                  ))}
                </Select>

                <ToggleButtonGroup
                  size="small"
                  value={viewMode}
                  exclusive
                  onChange={(_e, v) => v && setViewMode(v as ViewMode)}
                  data-testid="view-toggle"
                >
                  <ToggleButton value="grid" aria-label="Grid view">
                    <ViewModule fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="list" aria-label="List view">
                    <ViewList fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="image" aria-label="Image view">
                    <ImageIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </>
            )}

            <Button
              variant="outlined"
              size="small"
              onClick={() => setSaveDialogOpen(true)}
              disabled={!canSave}
              startIcon={<BookmarkBorder />}
              data-testid="save-search-button"
            >
              Save search
            </Button>
          </Box>

          {activeTab === 'objects' && activeFilterCount > 0 && (
            <Typography
              sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 2 }}
              data-testid="narrowed-hint"
            >
              {baselineTotal !== null && baselineTotal > total
                ? `Narrowed from ${baselineTotal}`
                : `${activeFilterCount} filter${activeFilterCount !== 1 ? 's' : ''} applied`}
            </Typography>
          )}

          {activeTab === 'users' && (
            <UsersTabPanel
              results={userResults}
              total={userTotal}
              isSearching={isSearchingUsers}
              error={userSearchError}
              hasIntent={hasIntent}
              searchTerm={searchTerm}
              onSelect={(u) => {
                // Backend enforces this format; we re-check before route-injecting.
                if (u.aliId && /^[A-Za-z0-9][A-Za-z0-9-]{1,18}[A-Za-z0-9]$/.test(u.aliId)) {
                  navigate(`/public/catalog/${u.aliId}`);
                }
              }}
              onClearQuery={handleClearQuery}
            />
          )}

          {activeTab === 'objects' && error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {activeTab === 'objects' && isSearching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          )}

          {activeTab === 'objects' && !isSearching && showEmptyState && (
            <EmptyState
              savedSearches={savedSearches}
              onApply={handleApplySavedSearch}
            />
          )}

          {activeTab === 'objects' && !isSearching && showNoResults && (
            <Box
              sx={{
                bgcolor: 'background.paper',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                p: 7,
                textAlign: 'center',
                boxShadow: 1,
              }}
              data-testid="search-no-results"
            >
              <Typography
                component="h2"
                sx={{ fontFamily: 'serif', fontWeight: 700, fontSize: '1.5rem', mb: 0.75 }}
              >
                No matches{searchTerm !== '' ? ` for “${searchTerm}”` : ''}
              </Typography>
              <Typography sx={{ color: 'text.secondary', maxWidth: 460, mx: 'auto', mb: 2.5 }}>
                Check your spelling, try a shorter query, or remove a filter.
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  handleClearAll();
                  handleClearQuery();
                }}
              >
                Clear search
              </Button>
            </Box>
          )}

          {activeTab === 'objects' && !isSearching && hasIntent && searchResults.length > 0 && (
            <>
              {(() => {
                const mappedArtworks = searchResults.map((r) => ({
                  artworkId: r.catalogItemId,
                  userId: r.userId,
                  title: r.title,
                  creator: r.creators?.[0] || '',
                  coverImageUrl: r.coverImageUrl,
                  aliCatalogNumber: r.aliCatalogNumber,
                  creationYear: r.creationYear,
                  visibility: r.visibility,
                }));
                const currentUserId =
                  (permissions as { userId?: string } | null)?.userId ?? null;
                return viewMode === 'list' ? (
                  <SearchResultsList
                    artworks={mappedArtworks}
                    isBookmarked={isBookmarked}
                    toggleBookmark={toggleBookmark}
                    currentUserId={currentUserId}
                  />
                ) : (
                  <SearchResultsGrid
                    artworks={mappedArtworks}
                    variant={viewMode === 'image' ? 'image' : 'grid'}
                    isBookmarked={isBookmarked}
                    toggleBookmark={toggleBookmark}
                    currentUserId={currentUserId}
                  />
                );
              })()}

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CatalogPagination
                    count={totalPages}
                    page={page + 1}
                    onChange={(_e, newPage) => setPage(newPage - 1)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <SaveSearchDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onSave={handleSaveSearch}
        suggestedName={searchTerm || 'Untitled search'}
      />
    </Container>
  );
};

/**
 * Empty-state panel shown when the user has no query and no filters active.
 * Surfaces saved searches so the user can one-click resume prior work.
 * A "recent searches" tile row would require a client-side history log
 * that doesn't exist yet — left for a follow-up phase.
 */
const EmptyState: React.FC<{
  savedSearches: Array<{
    searchId: string;
    name: string;
    searchParams: { q?: string; scopes?: string };
  }>;
  onApply: (s: {
    searchId: string;
    name: string;
    searchParams: { q?: string; scopes?: string };
  }) => void;
}> = ({ savedSearches, onApply }) => (
  <>
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        p: 7,
        textAlign: 'center',
        boxShadow: 1,
      }}
      data-testid="search-empty-state"
    >
      <Typography
        component="h2"
        sx={{ fontFamily: 'serif', fontWeight: 700, fontSize: '1.5rem', mb: 0.75 }}
      >
        Find artwork, creators, and users
      </Typography>
      <Typography sx={{ color: 'text.secondary', maxWidth: 460, mx: 'auto' }}>
        Search by title, catalog ID, ALI ID, username, or full name — or open a filter to browse.
      </Typography>
    </Box>
    {savedSearches.length > 0 && (
      <Box sx={{ mt: 3 }} data-testid="empty-saved-searches">
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'text.secondary',
            mb: 1.25,
          }}
        >
          Saved searches
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 1.25,
          }}
        >
          {savedSearches.slice(0, 6).map((s) => (
            <Paper
              key={s.searchId}
              elevation={0}
              onClick={() => onApply(s)}
              data-testid={`saved-search-card-${s.searchId}`}
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover': { boxShadow: 1 },
              }}
            >
              <HistoryIcon fontSize="small" sx={{ color: 'primary.main' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 500 }} noWrap>
                  {s.name}
                </Typography>
                {s.searchParams.q && (
                  <Typography
                    sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                    noWrap
                  >
                    {s.searchParams.q}
                  </Typography>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    )}
  </>
);

export default SearchPage;
